// Local draft autosave for the email composer.
//
// Why: Joey Ellis reported (2026-06-26) that composing in the OS
// doesn't auto-save, and that the only "keep my work" path (Save to
// Drafts) yanked him out to Outlook. This util persists the
// in-progress draft to localStorage so dismissing the composer
// (Cancel / Esc / backdrop click) never loses what was typed —
// reopening the composer for the same record or reply restores it.
// The draft is cleared on a successful send.
//
// Per the localStorage scope rule (CLAUDE.md "Coding conventions"):
// an in-progress email draft is per-device UI state that's fine to
// lose on a cache clear, so localStorage is the correct home — not
// the cross-device server-side preference store. (The advisor who
// wants a durable, cross-device draft uses Save to Drafts, which
// lands it in their Outlook Drafts folder via Graph.)
//
// Pure functions only; no React. Uses the same globalThis-typed
// storage access pattern as email-selected-mailbox.ts /
// email-folder-prefs.ts so a test importing this file doesn't drag
// the api module (and its Worker-typed fetch usage) into the root
// tsconfig project.

const STORAGE_KEY = "stewardship-os.email.composer-drafts.v1";
// Cap the number of stored drafts so a long-lived browser never grows
// the entry unbounded. Oldest (by savedAt) are pruned first.
const MAX_DRAFTS = 25;

export interface ComposerDraft {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  savedAt: number;
  // The recipients the composer was OPENED with when this draft was
  // started ("Email selected (3)" hands in three addresses; a contact
  // page hands in one; "New email" hands in none). Recorded so a later
  // open can tell whether the caller is asking for the same audience
  // this draft was written for — see resolveRestoredRecipients. Absent
  // on drafts saved before 2026-08-21.
  openedWithTo?: string[];
}

// What identifies the composer context. A reply restores onto the
// same reply; a compose from a work record restores onto that record;
// a record compose restores onto the same household / contact; a
// context-less "New email" shares one "new" slot.
export interface DraftKeyInput {
  householdId?: string | null;
  contactId?: string | null;
  replyToMessageId?: string | null;
  // The Processing / Opportunity record the composer was opened from,
  // when it was opened from one. Two deals in the same household are
  // two different pieces of work, and a half-written note about one of
  // them has no business reappearing on the other — which is exactly
  // what a household-only key did (Nate Swartz, 2026-08-20).
  recordEntityType?: string | null;
  recordEntityId?: string | null;
}

export function draftKeyFor(input: DraftKeyInput): string {
  if (input.replyToMessageId) return `reply:${input.replyToMessageId}`;
  if (input.recordEntityType && input.recordEntityId) {
    return `${input.recordEntityType}:${input.recordEntityId}`;
  }
  if (input.householdId) return `household:${input.householdId}`;
  if (input.contactId) return `contact:${input.contactId}`;
  return "new";
}

type DraftMap = Record<string, ComposerDraft>;

interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getStorage(): BrowserStorage | null {
  const g = globalThis as unknown as { localStorage?: BrowserStorage };
  return g.localStorage ?? null;
}

function readMap(): DraftMap {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as DraftMap;
  } catch {
    return {};
  }
}

function writeMap(map: DraftMap): void {
  const storage = getStorage();
  if (!storage) return;
  // Prune to the most-recently-saved MAX_DRAFTS entries.
  const entries = Object.entries(map);
  if (entries.length > MAX_DRAFTS) {
    entries.sort((a, b) => b[1].savedAt - a[1].savedAt);
    map = Object.fromEntries(entries.slice(0, MAX_DRAFTS));
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / SecurityError
  }
}

export function loadDraft(key: string): ComposerDraft | null {
  const map = readMap();
  return map[key] ?? null;
}

// Persist a draft for `key`. The caller passes the live composer
// state; we stamp savedAt here so timing isn't the caller's concern.
export function saveDraft(
  key: string,
  draft: Omit<ComposerDraft, "savedAt">,
): void {
  const map = readMap();
  map[key] = { ...draft, savedAt: Date.now() };
  writeMap(map);
}

export function clearDraft(key: string): void {
  const map = readMap();
  if (key in map) {
    delete map[key];
    writeMap(map);
  }
}

// Same set of addresses, ignoring order and case. Used to decide
// whether a restored draft was written for the audience the composer
// is being opened for now.
function sameAudience(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a.map((s) => s.trim().toLowerCase()));
  for (const addr of b) {
    if (!left.has(addr.trim().toLowerCase())) return false;
  }
  return true;
}

// Which recipients the reopened composer should show, and why.
export type RestoredRecipientSource =
  // No saved draft — the caller's prefill, as always.
  | "prefill"
  // Saved draft written for this same audience: keep it, including any
  // addresses the user added or removed by hand.
  | "draft"
  // Saved draft written for a DIFFERENT audience than the caller just
  // asked for. The caller wins; the draft's words are still restored.
  | "prefill_replaces_draft";

/**
 * Resolve the To line when reopening the composer over a saved draft.
 *
 * THE BUG THIS EXISTS TO STOP (Nate Swartz, 2026-08-20). Nate checked
 * three parties on a Processing record and hit "Email selected (3)";
 * the composer opened addressed to one person. The composer had found
 * an older unfinished draft on that household — addressed to just that
 * one person — and restored its recipient list over the three he had
 * picked seconds earlier. The draft-restored banner was the only clue,
 * and it reads as reassurance, not as a warning that his audience was
 * swapped out. Silently emailing a smaller set of people than the user
 * selected is the worst failure this composer can have.
 *
 * The rule: an explicit audience always beats a remembered one. What
 * the user WROTE (subject + body) is still restored — that is the
 * whole point of autosave — but who it goes to is whatever they just
 * asked for. When the caller names no audience (a bare "New email"),
 * the draft's own recipients stand.
 *
 * Drafts saved before this field existed carry no `openedWithTo`; they
 * take the same path as a mismatch, which is the safe direction.
 */
export function resolveRestoredRecipients(
  prefillTo: string[] | undefined,
  draft: Pick<ComposerDraft, "to" | "openedWithTo"> | null,
): { to: string[]; source: RestoredRecipientSource } {
  const prefill = prefillTo ?? [];
  if (!draft) return { to: prefill, source: "prefill" };
  if (draft.openedWithTo && sameAudience(draft.openedWithTo, prefill)) {
    return { to: draft.to, source: "draft" };
  }
  if (prefill.length > 0) {
    return { to: prefill, source: "prefill_replaces_draft" };
  }
  return { to: draft.to, source: "draft" };
}

// Collapse an HTML body to comparable plain text: strip tags, collapse
// whitespace, trim. Used so an unedited seed (prefill + signature)
// doesn't count as a draft, and so a contentEditable's cosmetic HTML
// reformatting on focus isn't mistaken for a real edit.
function bodyToText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sameRecipients(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// True when the current composer state has meaningful edits over the
// seeded baseline (the prefill + signature the composer opens with).
// Only meaningful drafts get persisted; otherwise we clear, so closing
// an untouched composer leaves no stale draft to restore.
export function draftDiffersFromBaseline(
  current: Omit<ComposerDraft, "savedAt">,
  baseline: Omit<ComposerDraft, "savedAt">,
): boolean {
  if (!sameRecipients(current.to, baseline.to)) return true;
  if (!sameRecipients(current.cc, baseline.cc)) return true;
  if (!sameRecipients(current.bcc, baseline.bcc)) return true;
  if (current.subject.trim() !== baseline.subject.trim()) return true;
  if (bodyToText(current.bodyHtml) !== bodyToText(baseline.bodyHtml)) {
    return true;
  }
  return false;
}
